import React, { useState, useCallback } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Menu, CalendarRange, NotebookPen, Hash, Settings2, Plus, X, LogOut } from 'lucide-react'
import pkg from '../../package.json'
import { useSections, useCreateSection, useDeleteSection } from '../api/hooks'
import { useUIStore } from '../store/ui'
import { useToast } from './primitives/Toast'
import { useAuthStore } from '../store/auth'
import { logoutAPI } from '../api/auth'

const TAGLINE = 'deliberate thought'

export function Sidebar() {
  const sidebarOpen = useUIStore((s) => s.sidebarOpen)
  const toggleSidebar = useUIStore((s) => s.toggleSidebar)
  const notesPanelOpen = useUIStore((s) => s.notesPanelOpen)
  const toggleNotesPanel = useUIStore((s) => s.toggleNotesPanel)
  const openNotesPanel = useUIStore((s) => s.openNotesPanel)
  const { data: sections = [] } = useSections()
  const createSection = useCreateSection()
  const deleteSection = useDeleteSection()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const onNotes = useLocation().pathname.startsWith('/notes')

  const [newSectionName, setNewSectionName] = useState('')
  const [addingSection, setAddingSection] = useState(false)

  const handleSignOut = useCallback(() => {
    // Fire-and-forget: clear local session immediately, navigate, then the
    // server invalidation completes in the background.
    logoutAPI()
    useAuthStore.getState().clear()
    navigate('/login')
  }, [navigate])

  // Notes is a panel, not a page: from elsewhere it opens the list and jumps
  // to the last note; from within notes the row folds the list away again.
  const handleNotesClick = useCallback(() => {
    if (onNotes) {
      toggleNotesPanel()
      return
    }
    openNotesPanel()
    navigate('/notes')
  }, [onNotes, toggleNotesPanel, openNotesPanel, navigate])

  const handleCreateSection = () => {
    const name = newSectionName.trim()
    if (!name) return
    const maxPos = sections.length > 0 ? Math.max(...sections.map((s) => s.position)) : 0
    createSection.mutate(
      { name, position: maxPos + 1 },
      {
        onSuccess: () => {
          setNewSectionName('')
          setAddingSection(false)
          showToast(`Section "${name}" created`, 'success')
        },
        onError: () => showToast('Failed to create section', 'error'),
      }
    )
  }

  if (!sidebarOpen) {
    return (
      <div
        style={{
          width: '40px',
          height: '100%',
          borderRight: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '12px 0',
          background: 'var(--color-paper)',
        }}
      >
        <button
          onClick={toggleSidebar}
          title="Open sidebar"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-ink-muted)',
            padding: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Menu size={14} strokeWidth={1.5} />
        </button>
      </div>
    )
  }

  return (
    <aside
      style={{
        width: '220px',
        minWidth: '220px',
        borderRight: '1px solid var(--color-border)',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--color-paper)',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* App header */}
      <div
        style={{
          padding: '20px 18px 16px',
          position: 'relative',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: '1.375rem',
            fontWeight: 600,
            color: 'var(--color-ink)',
            letterSpacing: '-0.02em',
          }}
        >
          todux
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: '12px',
            marginTop: '2px',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              fontSize: '0.75rem',
              fontWeight: 400,
              color: 'var(--color-ink-faint)',
            }}
          >
            {TAGLINE}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.6875rem',
              color: 'var(--color-ink-faint)',
              letterSpacing: '0.02em',
            }}
          >
            v{pkg.version}
          </div>
        </div>
        <button
          onClick={toggleSidebar}
          title='Collapse sidebar'
          style={{
            position: 'absolute',
            top: '16px',
            right: '14px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-ink-faint)',
            padding: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChevronLeft size={14} strokeWidth={1.5} />
        </button>
      </div>

      {/* Navigation */}
      <nav style={{ padding: '8px 0', flex: 1, overflowY: 'auto' }}>
        <SidebarNavItem to='/week' label='Week' icon={<CalendarRange size={14} strokeWidth={1.5} />} />

        {/* Sections */}
        {sections.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <div className='eyebrow' style={{ padding: '16px 18px 8px' }}>
              Sections
            </div>
            {[...sections]
              .sort((a, b) => a.position - b.position)
              .map((section) => (
                <div key={section.id} style={{ display: 'flex', alignItems: 'center' }}>
                  <SidebarNavItem to={`/sections/${section.id}`} label={section.name} icon={<Hash size={14} strokeWidth={1.5} />} style={{ flex: 1 }} />
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete section "${section.name}"?`)) {
                        deleteSection.mutate(section.id, {
                          onError: () => showToast('Failed to delete section', 'error'),
                        });
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-ink-faint)',
                      padding: '4px 8px',
                      opacity: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    className='section-delete-btn'
                    title='Delete section'
                  >
                    <X size={12} strokeWidth={1.5} />
                  </button>
                </div>
              ))}
          </div>
        )}

        <div style={{ height: '12px' }} />
        <SidebarNotesRow active={onNotes} expanded={notesPanelOpen} onClick={handleNotesClick} />
      </nav>

      {/* Bottom actions */}
      <div style={{ borderTop: '1px solid var(--color-border)', padding: '8px 0 12px' }}>
        {addingSection ? (
          <div style={{ display: 'flex', gap: '4px', padding: '6px 18px' }}>
            <input
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateSection();
                if (e.key === 'Escape') {
                  setAddingSection(false);
                  setNewSectionName('');
                }
              }}
              placeholder='Section name...'
              autoFocus
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--color-accent)',
                padding: '4px 0',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.8125rem',
                color: 'var(--color-ink)',
                outline: 'none',
              }}
            />
            <button
              onClick={handleCreateSection}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-accent)',
                padding: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Plus size={12} strokeWidth={1.5} />
            </button>
          </div>
        ) : (
          <SidebarActionRow label='New Section' icon={<Plus size={14} strokeWidth={1.5} />} onClick={() => setAddingSection(true)} />
        )}

        <SidebarNavItem to='/settings' label='Settings' icon={<Settings2 size={14} strokeWidth={1.5} />} />
        <SidebarActionRow label='Sign out' icon={<LogOut size={14} strokeWidth={1.5} />} onClick={handleSignOut} />
      </div>

      <style>{`
        .section-delete-btn:hover { opacity: 1 !important; color: #B43A2C !important; }
        aside:hover .section-delete-btn { opacity: 0.4; }
        .sidebar-nav-link:hover { color: var(--color-ink) !important; }
        .sidebar-action-row:hover { color: var(--color-ink) !important; }
      `}</style>
    </aside>
  );
}

interface SidebarNavItemProps {
  to: string
  label: string
  icon: React.ReactNode
  style?: React.CSSProperties
}

function SidebarNavItem({ to, label, icon, style: extraStyle }: SidebarNavItemProps) {
  return (
    <NavLink
      to={to}
      className="sidebar-nav-link"
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        // Active: inset marker; inactive: full-width padding
        marginLeft: isActive ? '8px' : '0',
        marginRight: isActive ? '8px' : '0',
        padding: isActive ? '6px 10px' : '6px 18px',
        borderRadius: isActive ? '3px' : '0',
        fontFamily: 'var(--font-serif)',
        fontSize: '0.9375rem',
        color: isActive ? 'var(--color-ink)' : 'var(--color-ink-muted)',
        textDecoration: 'none',
        background: isActive ? 'var(--color-paper-dark)' : 'transparent',
        fontWeight: isActive ? 500 : 400,
        transition: 'color 0.1s',
        ...extraStyle,
      })}
    >
      {({ isActive }) => (
        <>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              width: '14px',
              color: isActive ? 'var(--color-ink-muted)' : 'var(--color-ink-faint)',
              flexShrink: 0,
            }}
          >
            {icon}
          </span>
          {label}
        </>
      )}
    </NavLink>
  )
}

interface SidebarNotesRowProps {
  /** The notes route is open (the row reads as the current section). */
  active: boolean
  /** The notes list panel is showing beside the sidebar. */
  expanded: boolean
  onClick: () => void
}

// The Notes entry: a nav row that opens the notes list panel rather than
// navigating to a page of its own.
function SidebarNotesRow({ active, expanded, onClick }: SidebarNotesRowProps) {
  return (
    <button
      onClick={onClick}
      className='sidebar-action-row'
      aria-expanded={expanded}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: active ? 'calc(100% - 16px)' : '100%',
        marginLeft: active ? '8px' : '0',
        marginRight: active ? '8px' : '0',
        padding: active ? '6px 10px' : '6px 18px',
        borderRadius: active ? '3px' : '0',
        fontFamily: 'var(--font-serif)',
        fontSize: '0.9375rem',
        color: active ? 'var(--color-ink)' : 'var(--color-ink-muted)',
        background: active ? 'var(--color-paper-dark)' : 'none',
        fontWeight: active ? 500 : 400,
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'color 0.1s',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          width: '14px',
          color: active ? 'var(--color-ink-muted)' : 'var(--color-ink-faint)',
          flexShrink: 0,
        }}
      >
        <NotebookPen size={14} strokeWidth={1.5} />
      </span>
      Notes
      <span
        style={{
          marginLeft: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          color: 'var(--color-ink-faint)',
          transform: expanded ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.15s',
        }}
      >
        <ChevronRight size={12} strokeWidth={1.5} />
      </span>
    </button>
  )
}

interface SidebarActionRowProps {
  label: string
  icon: React.ReactNode
  onClick: () => void
}

// A button styled to look identical to a nav item row (used for "+ New Section")
function SidebarActionRow({ label, icon, onClick }: SidebarActionRowProps) {
  return (
    <button
      onClick={onClick}
      className="sidebar-action-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        padding: '6px 18px',
        fontFamily: 'var(--font-serif)',
        fontSize: '0.9375rem',
        color: 'var(--color-ink-muted)',
        fontWeight: 400,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'color 0.1s',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          width: '14px',
          color: 'var(--color-ink-faint)',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      {label}
    </button>
  )
}

